import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    IconButton,
    Alert,
    Snackbar,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { ToolPageShell } from "./ToolPageShell";
import { TOOLS } from "../util/config";
import { colors } from "../theme/theme";
import { pdfApi, statsApi } from "../api/client";

const tool = TOOLS.find((t) => t.id === "image-to-pdf");

export const ImageToPdfPage = () => {
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const showSnackbar = (message, severity) => setSnackbar({ open: true, message, severity });

    const onDrop = useCallback((acceptedFiles) => {
        const file = acceptedFiles[0];
        if (file && file.type.startsWith("image/")) {
            setImage(file);
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
            showSnackbar("Image uploaded successfully!", "success");
        } else {
            showSnackbar("Please upload a valid image file", "error");
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { "image/*": [".png", ".jpg", ".jpeg", ".gif", ".webp"] },
        multiple: false,
    });

    const convertToPdf = async () => {
        if (!image) return showSnackbar("Please upload an image first", "error");
        setIsConverting(true);
        try {
            const blob = await pdfApi.imageToPdf(image);
            statsApi.logExport(tool.id, blob.size);
            showSnackbar("PDF created and downloaded successfully!", "success");
        } catch (error) {
            console.error(error);
            showSnackbar("Failed to convert image to PDF", "error");
        } finally {
            setIsConverting(false);
        }
    };

    const clearImage = () => {
        setImage(null);
        setImagePreview(null);
    };

    return (
        <ToolPageShell
            tool={tool}
            tips={[
                "PNG, JPG, JPEG, GIF, and WEBP are supported.",
                "The image is centered on an A4 page at the source resolution.",
                "Large images are automatically scaled to fit the page.",
            ]}
        >
            {!imagePreview ? (
                <Box
                    {...getRootProps()}
                    sx={{
                        border: "2px dashed",
                        borderColor: isDragActive ? "primary.main" : colors.border,
                        borderRadius: 2,
                        padding: 6,
                        textAlign: "center",
                        cursor: "pointer",
                        bgcolor: isDragActive ? colors.accentSoft : colors.surfaceAlt,
                        transition: "all 0.2s",
                        "&:hover": { borderColor: "primary.main", bgcolor: colors.accentSoft },
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUploadIcon sx={{ fontSize: 56, color: isDragActive ? "primary.main" : colors.textMuted, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {isDragActive ? "Drop your image here" : "Drag & drop an image"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textMuted, mt: 0.5 }}>
                        or click to browse files
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: "block", mt: 2 }}>
                        Supports: PNG · JPG · JPEG · GIF · WEBP
                    </Typography>
                </Box>
            ) : (
                <Box>
                    <Box sx={{ position: "relative", mb: 3, borderRadius: 2, overflow: "hidden", border: `1px solid ${colors.border}` }}>
                        <img
                            src={imagePreview}
                            alt="Preview"
                            style={{ width: "100%", maxHeight: 420, objectFit: "contain", backgroundColor: colors.surfaceAlt, display: "block" }}
                        />
                        <IconButton
                            sx={{
                                position: "absolute", top: 8, right: 8,
                                bgcolor: "rgba(0,0,0,0.6)", color: "white",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                            }}
                            onClick={clearImage}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Box display="flex" gap={2} flexDirection={{ xs: "column", sm: "row" }}>
                        <Button
                            variant="contained"
                            size="large"
                            fullWidth
                            onClick={convertToPdf}
                            disabled={isConverting}
                            startIcon={isConverting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                        >
                            {isConverting ? "Converting..." : "Convert to PDF"}
                        </Button>
                        <Button variant="outlined" size="large" onClick={clearImage}>
                            Choose another
                        </Button>
                    </Box>
                </Box>
            )}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </ToolPageShell>
    );
};
